import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, AlertColor, Button, Chip, Grid, TextField, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import strings from 'common/Translation/Translate';
import Form from 'components/Form';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import UnsavedChangesModal from 'components/UnsavedChangesModal';
import dayjs from 'dayjs';
import { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { removeErrorFieldsFromValues } from 'utils/validators';
import { ScrollTop } from 'utils/validators/HelperFunctions';
import { regex } from 'utils/validators/Validators';
import { pageSubtitleSx, pageTitleSx } from 'utils/styles/pageHeader';
import Navigation from '../../../utils/hooks/Navigation';
import { AddBeoForm } from './Forms/AddBeoForm';
import { AmendmentSection, type AmendmentSectionHandle } from './AmendmentSection';
import { EventDetailsTable } from './EventDetailTable';
import { formatBeoDate } from './EventDetailCard';
import LoyaltyProgramSection from './LoyaltyProgramSection';

import BeoActionButtons from './BeoActionButtons';

type refType = RefObject<Form | null | undefined>;

const formatBeoLastUpdated = (timestamp?: string | null) => {
  if (!timestamp) return null;
  const at = dayjs(timestamp);
  if (!at.isValid()) return null;
  const minutes = dayjs().diff(at, 'minute');
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  const hours = dayjs().diff(at, 'hour');
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = dayjs().diff(at, 'day');
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return at.format('D MMM YYYY');
};

const beoEditorFields = [
  'menu',
  'miscellaneous',
  'beverage',
  'program',
  'roomSetup',
  'audioVisual',
  'decor',
  'engineering',
  'security',
  'billing',
];

const AddBeo: FC = () => {
  const { id } = useParams() as any;
  const theme = useTheme();
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));

  let formRef: refType = useRef();

  const [pageResponse, setPageResponse] = useState<any>(null);
  const [eventDetails, setEventDetails] = useState<any>({});
  const [layout, setLayout] = useState<any>('');
  const [beoName, setBeoName] = useState<string>('');
  const [boardToRead, setBoardToRead] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);
  const [beoNameError, setBeoNameError] = useState<boolean>(false);
  const [boardToReadError, setBoardToReadError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Sync guard so a second Save click in the same tick cannot start another POST. */
  const savingRef = useRef(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const initialLoadingRef = useRef<boolean>(false);
  const hydratingEditorFieldsRef = useRef<Set<string>>(new Set(beoEditorFields));
  const [unsavedDialog, setUnsavedDialog] = useState<{
    open: boolean;
    action: (() => void | Promise<void>) | null;
  }>({ open: false, action: null });
  const [unsavedSaving, setUnsavedSaving] = useState<boolean>(false);
  const [showFormatTip, setShowFormatTip] = useState(true);
  const [includeFlagsSaving, setIncludeFlagsSaving] = useState(false);
  const amendmentSectionRef = useRef<AmendmentSectionHandle>(null);

  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const [beoId, setBeoId] = useState('');

  const markDirty = useCallback(() => {
    if (!initialLoadingRef.current) setIsDirty(true);
  }, []);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = useCallback((error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  }, []);

  const saveIncludeFlags = useCallback(
    async (formData?: any) => {
      if (!id || !pageResponse?.id) return;
      const payload = {
        includeBeoDetails: Boolean(formData?.includeBeoDetails),
        includeAmendments: Boolean(formData?.includeAmendments),
      };
      const previous = {
        includeBeoDetails: Boolean(pageResponse.includeBeoDetails),
        includeAmendments: Boolean(pageResponse.includeAmendments),
      };
      setIncludeFlagsSaving(true);
      try {
        await axiosInstance.patch(URLS.beoIncludeFlags(id), payload);
        setPageResponse((prev: any) => (prev ? { ...prev, ...payload } : prev));
      } catch (error: any) {
        handleCatch(error);
        setPageResponse((prev: any) => (prev ? { ...prev, ...previous } : prev));
      } finally {
        setIncludeFlagsSaving(false);
      }
    },
    [id, pageResponse?.id, pageResponse?.includeBeoDetails, pageResponse?.includeAmendments, handleCatch],
  );

  const handleFormOnChange = useCallback(
    (field: string, _value?: string | boolean, formData?: any) => {
      if (field === 'includeBeoDetails' || field === 'includeAmendments') {
        void saveIncludeFlags(formData);
        return;
      }
      if (pageResponse?.sentToStaff || pageResponse?.isPastBooking) return;
      if (hydratingEditorFieldsRef.current.has(field)) {
        hydratingEditorFieldsRef.current.delete(field);
        return;
      }
      markDirty();
    },
    [markDirty, pageResponse?.sentToStaff, pageResponse?.isPastBooking, saveIncludeFlags],
  );

  const getInitialData = useCallback(async () => {
    initialLoadingRef.current = true;
    setIsLoading(true);
    try {
      const { data } = await axiosInstance.get(URLS.beoData(id));

      setBeoId(data.id);
      setBeoName(data.beoName ?? '');
      setBoardToRead(data.boardToRead ?? '');
      setLayout(data.layout);
      const normalizedEventDetails = data.eventDetails.map((event: any) => ({
        ...event,
        timeSlot: `${event.startTime} - ${event.endTime}`,
        bookingLineItemId: event.id,
        layoutId: event.selectedLayout?.value ?? null,
        selectedLayout: event.selectedLayout?.label ?? null,
      }));
      setEventDetails(normalizedEventDetails);
      setPageResponse({
        ...data,
        amendments: data.amendments.map((d: any) => ({
          id: d.id,
          datetime: d.createdAt,
          summary: d.amendment,
          createdAt: d.createdAt,
          createdBy: d.amendedBy,
        })),
      });
      setIsDirty(false);
      hydratingEditorFieldsRef.current = new Set(beoEditorFields);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      initialLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const handleSave = async (e: any, preview_beo: boolean): Promise<boolean> => {
    e.preventDefault();
    if (savingRef.current || isLoading) return false;
    savingRef.current = true;
    try {
      const { getFormData } = formRef.current!;
      const { formData, isFormValid } = getFormData();
      const fields = removeErrorFieldsFromValues(formData);
      setHasError(false);

      if (!isFormValid) {
        setHasError(true);
        return false;
      }

      if (beoName === '' || beoName?.length > 200) {
        beoName === '' && setBeoNameError(true);
        ScrollTop(0);
        return false;
      }

      if (boardToRead === '' || boardToRead?.length > 200) {
        boardToRead === '' && setBoardToReadError(true);
        ScrollTop(0);
        return false;
      }

      if (!regex.string.test(beoName) || !regex.string.test(boardToRead)) {
        ScrollTop(0);
        return false;
      }

      const requestBody = {
        ...fields,
        beoName: beoName,
        boardToRead: boardToRead,
        layout: layout,
        eventDetails: eventDetails.map((e: any) => ({
          bookingLineItemId: e.bookingLineItemId,
          guaranteedGuests: e.guaranteedGuests,
          layoutId: e.layoutId,
        })),
      };

      setIsLoading(true);
      const { status } = await axiosInstance.post(URLS.beoData(id), requestBody);

      if (status === 200) {
        if (!preview_beo) {
          setMessage({ display: true, severity: 'success', message: strings.common_update_message });
          await getInitialData();
        }
        return true;
      }
      return false;
    } catch (error: any) {
      handleCatch(error);
      return false;
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };

  const runWithSaveIfDirty = (action: () => void | Promise<void>) => {
    if (isDirty) {
      setUnsavedDialog({ open: true, action });
      return;
    }
    void action();
  };

  const closeUnsavedDialog = () => setUnsavedDialog({ open: false, action: null });

  const handleSaveAndContinue = async () => {
    const action = unsavedDialog.action;
    try {
      setUnsavedSaving(true);
      const saved = await handleSave({ preventDefault: () => undefined }, false);
      if (saved) {
        closeUnsavedDialog();
        if (action) await action();
      }
    } finally {
      setUnsavedSaving(false);
    }
  };

  const handleDiscardAndContinue = async () => {
    const action = unsavedDialog.action;
    closeUnsavedDialog();
    await getInitialData();
    if (action) await action();
  };

  const getLabel = () => {
    if (pageResponse.isPastBooking) return strings.BEO_details_text;
    else if (pageResponse.id) return strings.update_BEO_text;
    else return strings.add_BEO_Text;
  };

  const headerMeta = useMemo(() => {
    if (!pageResponse) return '';
    const parts: string[] = [];
    if (pageResponse.beoReferenceNo) parts.push(pageResponse.beoReferenceNo);
    if (pageResponse.id != null && Number(pageResponse.version) > 0) {
      parts.push(`${strings.version} ${pageResponse.version}`);
    }
    const relative = formatBeoLastUpdated(pageResponse.editedAt || pageResponse.createdAt);
    if (relative) parts.push(`${strings.beo_last_updated} ${relative}`);
    return parts.join(' . ');
  }, [pageResponse]);

  const completedBanner = useMemo(() => {
    if (!pageResponse?.isPastBooking) return null;
    const titleParts = [strings.beo_completed_banner_title];
    if (pageResponse.sentToStaff) titleParts.push(strings.beo_completed_banner_distributed);
    const latestDate = (pageResponse.eventDetails || [])
      .map((event: any) => dayjs(event.eventDate))
      .filter((d: dayjs.Dayjs) => d.isValid())
      .reduce((latest: dayjs.Dayjs | null, d: dayjs.Dayjs) => (!latest || d.isAfter(latest, 'day') ? d : latest), null);
    const dateLabel = latestDate ? formatBeoDate(latestDate) : '';
    return {
      title: titleParts.join(' · '),
      body: dateLabel
        ? strings.beo_completed_banner_body_dated.replace('{date}', dateLabel)
        : strings.beo_completed_banner_body,
    };
  }, [pageResponse]);

  return (
    <>
      {isLoading && <PageLoader />}
      {pageResponse !== null && (
        <>
          {pageResponse.isPastBooking && beoId === null ? (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm="auto">
                <Alert severity="warning" sx={{ width: 'max-content' }}>
                  {strings.no_beo_added_text}
                </Alert>
              </Grid>
              <Grid item>
                <Navigation url={-1} />
              </Grid>
            </Grid>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: { xs: 'calc(100vh - 66px)', sm: 'calc(100vh - 80px)' },
                minHeight: 0,
                mx: { xs: -1.5, sm: -1.5 },
                my: { xs: -1.5, sm: -2 },
              }}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  px: { xs: 1.5, sm: 1.5 },
                  pt: { xs: 1.5, sm: 2 },
                  pb: 2,
                }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 2,
                  flexWrap: 'wrap',
                }}>
                <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
                  <Typography sx={pageTitleSx}>{getLabel()}</Typography>
                  {!pageResponse.isPastBooking && (
                    <Typography sx={{ ...pageSubtitleSx, mt: 0.25 }}>{strings.beo_page_subtitle}</Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    ml: 'auto',
                    flexShrink: 0,
                    maxWidth: '100%',
                  }}>
                  {pageResponse.id ? (
                    <Chip
                      size="small"
                      icon={
                        pageResponse.sentToStaff ? (
                          <CheckIcon />
                        ) : (
                          <AccessTimeIcon />
                        )
                      }
                      label={
                        pageResponse.sentToStaff
                          ? strings.beo_status_distributed
                          : strings.beo_status_not_distributed
                      }
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.1,
                        bgcolor: pageResponse.sentToStaff ? '#E6F4EA' : '#FFF4E5',
                        color: pageResponse.sentToStaff ? '#137333' : '#B06000',
                        '& .MuiChip-icon': {
                          fontSize: 14,
                          color: 'inherit',
                          ml: 0.75,
                          mr: -0.25,
                        },
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  ) : null}
                  {headerMeta ? (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: 'text.secondary',
                        textAlign: 'right',
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      }}>
                      {headerMeta}
                    </Typography>
                  ) : null}
                  <Navigation url={-1} />
                </Box>
              </Box>
              {message.display && (
                <Notification
                  isOpen={message.display}
                  message={message.message}
                  severity={message.severity as AlertColor}
                  closeNotification={closeNotification}
                />
              )}
              {completedBanner && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                    {completedBanner.title}
                  </Typography>
                  <Typography sx={{ mt: 0.35, fontSize: 13, lineHeight: 1.45 }}>
                    {completedBanner.body}
                  </Typography>
                </Alert>
              )}
              {pageResponse.sentToStaff && !pageResponse.isPastBooking && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                    px: 1.75,
                    py: 1.5,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.22),
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}>
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 36,
                      color: 'primary.main',
                      flexShrink: 0,
                      alignSelf: { xs: 'flex-start', sm: 'center' },
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: 'primary.main',
                        fontWeight: 700,
                        fontSize: 15,
                        lineHeight: 1.3,
                      }}>
                      {strings.beo_locked_banner_title}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.35,
                        fontSize: 13,
                        lineHeight: 1.45,
                        color: 'text.secondary',
                      }}>
                      {strings.beo_locked_banner_body}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => amendmentSectionRef.current?.openComposer()}
                    disabled={isLoading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ml: { xs: 0, sm: 'auto' },
                    }}>
                    {strings.beo_add_amendment}
                  </Button>
                </Box>
              )}
              <Grid container alignItems={'center'} spacing={screenSizeDownMd ? 0 : 2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    error={beoName?.length > 200 || beoNameError || !regex.string.test(beoName)}
                    helperText={
                      <Box display={'flex'} justifyContent={beoNameError ? 'space-between' : 'flex-end'}>
                        {beoNameError ? (
                          <Typography fontSize={'12px'}>{strings.requiredText}</Typography>
                        ) : !regex.string.test(beoName) ? (
                          <Typography fontSize={'12px'}>{strings.validateStringErrMsg}</Typography>
                        ) : (
                          ''
                        )}
                        {<Typography fontSize={'12px'}>{`${beoName?.length}/200`}</Typography>}
                      </Box>
                    }
                    sx={{
                      mb: 2,
                      '& .MuiFormHelperText-root': {
                        mr: 0,
                      },
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'action.hover',
                      },
                    }}
                    label={`${strings.beo_name} *`}
                    fullWidth
                    value={beoName}
                    disabled={Boolean(pageResponse.isPastBooking || pageResponse.sentToStaff)}
                    onChange={(e: any) => {
                      setBeoNameError(e.target.value === '');
                      setBeoName(e.target.value);
                      markDirty();
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    error={boardToRead?.length > 200 || boardToReadError || !regex.string.test(boardToRead)}
                    helperText={
                      <Box display={'flex'} justifyContent={boardToReadError ? 'space-between' : 'flex-end'}>
                        {boardToReadError && <Typography fontSize={'12px'}>{strings.requiredText}</Typography>}
                        {!regex.string.test(boardToRead) && (
                          <Typography fontSize={'12px'}>{strings.validateStringErrMsg}</Typography>
                        )}
                        <Typography fontSize={'12px'}>{`${boardToRead?.length}/200`}</Typography>
                      </Box>
                    }
                    sx={{
                      mb: 2,
                      '& .MuiFormHelperText-root': {
                        mr: 0,
                      },
                      '& .MuiInputBase-root.Mui-disabled': {
                        bgcolor: 'action.hover',
                      },
                    }}
                    label={`${strings.board_to_read} *`}
                    fullWidth
                    value={boardToRead}
                    disabled={Boolean(pageResponse.isPastBooking || pageResponse.sentToStaff)}
                    data-testid="boardToRead"
                    onChange={(e: any) => {
                      setBoardToReadError(e.target.value === '');
                      setBoardToRead(e.target.value);
                      markDirty();
                    }}
                  />
                </Grid>
              </Grid>

              {eventDetails?.length > 0 && (
                <EventDetailsTable
                  selectedEventDetails={eventDetails}
                  setSelectedEventDetails={(details: any) => {
                    setEventDetails(details);
                    markDirty();
                  }}
                  sentToStaff={pageResponse.sentToStaff}
                  isPastBooking={pageResponse.isPastBooking}
                />
              )}

              {pageResponse.loyaltyProgram !== null && Object.keys(pageResponse.loyaltyProgram)?.length > 0 && (
                <LoyaltyProgramSection loyaltyProgram={pageResponse.loyaltyProgram} />
              )}

              {/* Static Content */}
              {/*<BillingDetailsCard billingDetails={pageResponse.billingDetails} />*/}
              {/* <PaymentsAccordion
                payments={pageResponse.payments}
                paymentReceived={pageResponse.billingDetails.advanceReceived}
                paymentDue={pageResponse.billingDetails.paymentDue}
              /> */}
              {!pageResponse.isPastBooking && !pageResponse.sentToStaff && showFormatTip && (
                <Alert
                  severity="info"
                  onClose={() => setShowFormatTip(false)}
                  sx={{
                    mb: 1.5,
                    py: 0.5,
                    alignItems: 'center',
                    '& .MuiAlert-icon': { py: 0, alignSelf: 'center' },
                    '& .MuiAlert-message': { fontSize: 13, py: 0 },
                    '& .MuiAlert-action': { pt: 0, pl: 1, mr: -0.5, alignItems: 'center', alignSelf: 'center' },
                  }}>
                  {strings.copyFromWordText}
                </Alert>
              )}

              <Grid container columnSpacing={2} rowSpacing={1} sx={{ mb: 1 }}>
                <Form
                  hasError={hasError}
                  ref={formRef as RefObject<Form>}
                  model={AddBeoForm(
                    strings,
                    pageResponse.isPastBooking,
                    pageResponse.sentToStaff,
                    pageResponse.amendments.length > 0,
                    pageResponse,
                    includeFlagsSaving,
                  )}
                  values={pageResponse}
                  onChange={handleFormOnChange}
                />

                {pageResponse.sentToStaff && (
                  <Grid item xs={12}>
                    <AmendmentSection
                      ref={amendmentSectionRef}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                      getInitialData={getInitialData}
                      amendments={pageResponse.amendments}
                      isPastBooking={pageResponse.isPastBooking}
                      setMessage={setMessage}
                    />
                  </Grid>
                )}
              </Grid>
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  px: { xs: 1.5, sm: 1.5 },
                  py: 0.75,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: '0 -6px 16px rgba(0, 0, 0, 0.06)',
                }}>
                <BeoActionButtons
                  id={id}
                  beoId={pageResponse?.id}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  setMessage={setMessage}
                  handleCatch={handleCatch}
                  handleSave={handleSave}
                  fetchInitialData={getInitialData}
                  pageResponse={pageResponse}
                  runWithSaveIfDirty={runWithSaveIfDirty}
                />
              </Box>

              <UnsavedChangesModal
                open={unsavedDialog.open}
                onClose={closeUnsavedDialog}
                onSave={handleSaveAndContinue}
                onDiscard={handleDiscardAndContinue}
                loading={unsavedSaving}
                subtitle={strings.beoUnsavedChangesSubtitle}
                callout={strings.beoUnsavedChangesCallout}
                message={strings.beoUnsavedChangesBody}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default AddBeo;
